// ---------------------------------------------------------------------------
// Live monitoring API layer — SP-5f Task B1
// Zod schemas + TanStack Query hooks for snapshot + SSE ticket.
// ---------------------------------------------------------------------------

import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

export const SessionSchema = z.object({
  session_id: z.string(),
  operator_user_id: z.number(),
  operator_display_name: z.string(),
  mission_id: z.number().nullable(),
  pick_list_id: z.number().nullable(),
  pick_list_external_id: z.string().nullable(),
  pick_list_recipient: z.string().nullable(),
  current_task_id: z.number().nullable(),
  current_task_location: z.string().nullable(),
  current_task_item_name: z.string().nullable(),
  qty_required: z.number().nullable(),
  qty_picked: z.number().nullable(),
  fsm_state: z.string().nullable(),
  is_muted: z.boolean(),
  is_paused: z.boolean(),
  started_at: z.string(),
  last_event_at: z.string(),
  tasks_done: z.number().nullable(),
  tasks_total: z.number().nullable(),
});

export type SessionDto = z.infer<typeof SessionSchema>;

export const PickListLiteSchema = z.object({
  pick_list_id: z.number(),
  external_id: z.string(),
  recipient: z.string().nullable(),
  status: z.enum(['open', 'in_progress', 'fulfilled', 'confirmed', 'cancelled', 'archived']),
  total_missions: z.number(),
  active_missions: z.number(),
});

export type PickListLiteDto = z.infer<typeof PickListLiteSchema>;

export const SnapshotSchema = z.object({
  warehouse_id: z.number(),
  snapshot_at: z.string(),
  sessions: z.array(SessionSchema),
  pick_lists_in_progress: z.array(PickListLiteSchema),
});

export type SnapshotDto = z.infer<typeof SnapshotSchema>;

export const TicketSchema = z.object({
  ticket: z.string(),
  // sse_url is informational; the dashboard hard-codes /sse/live/stream.
  // Backend's LiveTicketController returns only {ticket, expires_at}, so sse_url
  // stays optional — required would have made the parse throw and the openStream
  // .catch() would loop forever (request ticket → parse fail → reconnect).
  sse_url: z.string().optional(),
  expires_at: z.string(),
});

export type TicketDto = z.infer<typeof TicketSchema>;

// ---------------------------------------------------------------------------
// useLiveSnapshot
// ---------------------------------------------------------------------------

/**
 * Fetches the live snapshot for a warehouse. No auto-refetch — the snapshot
 * is only used for initialisation; SSE events carry subsequent state.
 */
export function useLiveSnapshot(
  token: string | null,
  tenantCode: string | null,
  warehouseId: number | null,
) {
  return useQuery<SnapshotDto>({
    queryKey: ['admin', 'live', 'snapshot', warehouseId],
    enabled: token !== null && tenantCode !== null && warehouseId !== null,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE}/admin/live/snapshot?warehouse_id=${warehouseId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-Id': tenantCode!,
          },
        },
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      return SnapshotSchema.parse(await res.json());
    },
  });
}

// ---------------------------------------------------------------------------
// useLiveOperators — roster completo operatori del magazzino (anche offline)
// ---------------------------------------------------------------------------

export const LiveOperatorSchema = z.object({
  user_id: z.number(),
  display_name: z.string(),
  connected: z.boolean(),
  current_mission_id: z.number().nullable(),
  // SP-8c — avanzamento PERSISTITO della mission corrente (per le card offline).
  pick_list_id: z.number().nullable().optional(),
  pick_list_external_id: z.string().nullable().optional(),
  tasks_done: z.number().nullable().optional(),
  tasks_total: z.number().nullable().optional(),
  qty_assigned_total: z.number().nullable().optional(),
  qty_picked_total: z.number().nullable().optional(),
  // Ultima attività dell'operatore (da voice_sessions.last_seen_at), per le card offline.
  last_seen_at: z.string().nullable().optional(),
  // Missioni assigned/active dell'operatore oltre a quella corrente ("in coda").
  assigned_missions_remaining: z.number().nullable().optional(),
});

export type LiveOperatorDto = z.infer<typeof LiveOperatorSchema>;

const LiveOperatorsResponseSchema = z.object({ items: z.array(LiveOperatorSchema) });

/**
 * Tutti i voice-operator del magazzino (da tenant_memberships), a prescindere
 * dalla connessione. Serve a mostrare anche gli operatori NON collegati nella
 * vista live: lo stato "online/offline" si deriva incrociando questo roster
 * con le sessioni attive (presenza in `sessions` = online).
 */
export function useLiveOperators(
  token: string | null,
  tenantCode: string | null,
  warehouseId: number | null,
) {
  return useQuery<LiveOperatorDto[]>({
    queryKey: ['admin', 'live', 'operators', warehouseId],
    enabled: token !== null && tenantCode !== null && warehouseId !== null,
    // SP-8c — dati live: roster aggiornato spesso (lo stato online/offline
    // del singolo operatore arriva comunque in real-time via SSE; qui teniamo
    // fresca la lista di chi esiste nel magazzino).
    staleTime: 0,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/live/operators?warehouse_id=${warehouseId}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantCode! },
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      return LiveOperatorsResponseSchema.parse(await res.json()).items;
    },
  });
}

// ---------------------------------------------------------------------------
// useRequestLiveTicket
// ---------------------------------------------------------------------------

/**
 * Mutation: POST /v1/live/ticket (SP-4b route, NOT /admin/live/ticket).
 * Body: { warehouse_id: number }
 * Returns: TicketDto { ticket, sse_url, expires_at }
 */
export function useRequestLiveTicket(
  token: string | null,
  tenantCode: string | null,
) {
  return useMutation<TicketDto, Error, { warehouseId: number }>({
    mutationFn: async ({ warehouseId }) => {
      const res = await fetch(`${API_BASE}/live/ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token!}`,
          'X-Tenant-Id': tenantCode!,
          'Idempotency-Key': crypto.randomUUID(),
        },
        body: JSON.stringify({ warehouse_id: warehouseId }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }
      return TicketSchema.parse(await res.json());
    },
  });
}
